import { useState } from 'react';

import { Badge, IBadgeProps, Popover, PopoverTrigger, Tooltip } from '@restworld/ui-ds-base';
import { useResponsiveBreakPoints } from '@restworld/utils-common';

export interface AssociationBadgeProps {
  /**
   * Short name of the association, shown inside the badge (e.g. "APCI").
   * Comes from the `label` of the backing Variable, so it is already
   * human-readable content rather than an i18n key.
   */
  label: string;
  /**
   * Longer explanation of what the association is, shown in the card. Comes
   * from the `description` of the backing Variable. When both this and
   * `membershipLabel` are omitted, the badge renders without a card.
   */
  description?: string;
  /**
   * Association logo, from the `icon` of the backing Variable. Rendered at
   * 16px, so the asset must be icon-only: a lockup with the extended name is
   * unreadable at this size (verified on the July 2026 APCI carousels, where
   * the name wrapped onto three lines below 64px).
   *
   * A plain `img` rather than `next/image` on purpose, matching Avatar: the
   * URL is data, so it needs no per-app domain allowlist and it renders in
   * Storybook. A broken URL falls back to the text-only badge.
   */
  iconUrl?: string;
  /**
   * Optional line rendered above the description, used employer-side to state
   * the relationship rather than the entity (e.g. "Iscritto ad APCI").
   */
  membershipLabel?: string;
  /**
   * Where "find out more" leads, typically the association's partner landing
   * (`/partner/<key>`, the slug being the Variable key). Rendered as the last
   * line of the card. Requires `linkLabel`.
   */
  linkUrl?: string;
  /**
   * Translated label of the link (e.g. "Scopri di più"). A prop rather than a
   * literal so each app passes its own i18n key.
   */
  linkLabel?: string;
  variant?: IBadgeProps['variant'];
}

/**
 * Badge stating that a worker belongs to a professional association. Shown on
 * the worker's own profile (worker-app) and on the candidate card an employer
 * opens from a job position (employer-app).
 *
 * Deliberately decorative. This badge carries no ranking, filtering or
 * recommendation weight anywhere in the product, and must not acquire any:
 * membership is bought with an annual fee, so weighting it would sell a fast
 * lane to whichever candidates can afford one.
 *
 * Pure presentation. Whether a worker has declared an association, and whether
 * the viewer may see it, is decided by the calling component.
 *
 * On a pointer device the card opens on hover, the same way JobUpdatedBadge
 * does, and Tooltip content is hoverable so the cursor can travel from the
 * badge into the card and reach the "find out more" link without it closing.
 *
 * Below `lg` it opens on tap instead, via Popover. A Radix tooltip never opens
 * on touch, so on a phone the description would otherwise be unreachable, and
 * the brief asks for it there: "on mobile there is no hover, so it opens on
 * tap".
 */
export const AssociationBadge = ({
  label,
  description,
  iconUrl,
  membershipLabel,
  linkUrl,
  linkLabel,
  variant = 'secondary',
}: AssociationBadgeProps) => {
  const hasLink = Boolean(linkUrl && linkLabel);
  const { isDesktop } = useResponsiveBreakPoints();
  // The URL that failed, not a boolean: this instance is reused across
  // candidates (the employer candidate modal swaps the selected application
  // without unmounting), so a boolean would latch after one bad logo and keep
  // hiding every later one, including valid ones.
  const [failedIconUrl, setFailedIconUrl] = useState<string>();
  const showIcon = Boolean(iconUrl) && failedIconUrl !== iconUrl;

  const badgeEl = (
    <Badge variant={variant}>
      <span className="flex items-center gap-1">
        {showIcon && (
          <img
            src={iconUrl}
            alt=""
            aria-hidden
            className="h-4 w-4 object-contain"
            onError={() => setFailedIconUrl(iconUrl)}
          />
        )}
        <span>{label}</span>
      </span>
    </Badge>
  );

  if (!description && !membershipLabel && !hasLink) return badgeEl;

  const cardContent = (
    <div className="flex flex-col gap-1 text-left text-xs">
      {membershipLabel && <div className="font-semibold">{membershipLabel}</div>}
      {description && <div>{description}</div>}
      {hasLink && (
        <a
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 font-semibold underline underline-offset-2"
        >
          {linkLabel}
        </a>
      )}
    </div>
  );

  // Touch has no hover, so below `lg` the card opens on tap instead.
  if (!isDesktop) {
    return (
      // `panelClassName` rather than a `max-w` on the inner div: it lands on Radix's own
      // content box, which carries its own `p-6`, so the cap covers the padding too. Capping
      // the inner div instead lets the box grow to 320px + 48px ≈ 368px, which overflows a
      // 360px-class phone — and `align="start"` anchors it to the badge, not the screen.
      <Popover
        trigger={PopoverTrigger.CLICK}
        className="w-fit"
        panelClassName="max-w-[320px]"
        haveDefaultButtonStyle={false}
      >
        <Popover.Button>{badgeEl}</Popover.Button>
        <Popover.Panel>
          <div className="p-3">{cardContent}</div>
        </Popover.Panel>
      </Popover>
    );
  }

  return (
    // `w-fit` because Tooltip stretches its trigger to the full width of the
    // wrapper it sits in; without it the card anchors to the middle of the whole
    // row and its arrow points at empty space.
    <div className="w-fit">
      <Tooltip
        // High z so the card floats above the employer candidate modal and the
        // worker-app navbar; otherwise it is clipped by either.
        className="max-w-[320px] !z-[9999]"
        content={cardContent}
      >
        {badgeEl}
      </Tooltip>
    </div>
  );
};

export default AssociationBadge;
